import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { BiSearch, BiEdit, BiTrash } from 'react-icons/bi';
import { IoIosArrowDown } from 'react-icons/io';
import { 
  getShowroomCategory, 
  addShowroomCategory, 
  updateShowroomCategory,
  removeShowroomCategory 
} from '../slice/showroomCategorySlice';
import './Showroom.css';
import { toast } from 'react-toastify';
import ConfirmModal from '../common/ConfirmModal';

const ShowroomCategories = () => {
  const dispatch = useDispatch();
  const { data: categories = [], loading } = useSelector((state) => state.showroomCategory);

  const [activeTab, setActiveTab] = useState('All');
  const [searchTerm, setSearchTerm] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 10;

  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm, activeTab]);
  
  // Modal states
  const [showMainModal, setShowMainModal] = useState(false);
  const [showSubModal, setShowSubModal] = useState(false);
  const [showDropdown, setShowDropdown] = useState(false);
  const [deleteCatTarget, setDeleteCatTarget] = useState(null);
  const [editCatTarget, setEditCatTarget] = useState(null);
  const [isEditing, setIsEditing] = useState(false);

  // Form states
  const [formData, setFormData] = useState({
    id: null,
    category_type: 0,
    category: '',
    description: ''
  });

  useEffect(() => {
    dispatch(getShowroomCategory());
  }, [dispatch]);

  const handleInputChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleCreateSubmit = (e) => {
    e.preventDefault();
    if (!formData.category || !formData.description) {
      toast.error('Please fill all required fields');
      return;
    }
    if (isEditing && formData.id) {
      dispatch(updateShowroomCategory({ id: formData.id, data: formData }));
      toast.success('Category updated successfully');
    } else {
      dispatch(addShowroomCategory(formData));
    }
    setShowMainModal(false);
    setShowSubModal(false);
    setIsEditing(false);
    setFormData({ id: null, category_type: 0, category: '', description: '' });
  };

  const handleConfirmEdit = () => {
    if (editCatTarget) {
      setFormData({
        id: editCatTarget.id,
        category_type: editCatTarget.category_type,
        category: editCatTarget.category,
        description: editCatTarget.description
      });
      setIsEditing(true);
      if (editCatTarget.category_type === 0) {
        setShowMainModal(true);
      } else {
        setShowSubModal(true);
      }
      setEditCatTarget(null);
    }
  };

  const handleStatusToggle = (cat) => {
    const newStatus = cat.status === 1 ? 0 : 1;
    dispatch(updateShowroomCategory({ id: cat.id, data: { status: newStatus } }));
  };

  const handleDelete = (cat) => {
    setDeleteCatTarget(cat);
  };

  const handleConfirmDelete = () => {
    if (deleteCatTarget) {
      dispatch(removeShowroomCategory(deleteCatTarget.id));
      setDeleteCatTarget(null);
    }
  };

  const filteredCategories = (Array.isArray(categories) ? categories : []).filter((cat) => {
    if (activeTab === 'Main Category' && cat.category_type !== 0) return false;
    if (activeTab === 'Sub Category' && cat.category_type === 0) return false;
    if (searchTerm && !cat.category.toLowerCase().includes(searchTerm.toLowerCase())) return false;
    return true;
  });

  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentCategories = filteredCategories.slice(indexOfFirstItem, indexOfLastItem);

  const mainCategories = (Array.isArray(categories) ? categories : []).filter(c => c.category_type === 0);

  const formatDate = (dateString) => {
    if (!dateString) return { dateOnly: '-', timeOnly: '' };
    try {
      const date = new Date(dateString);
      if (isNaN(date.getTime())) return { dateOnly: dateString, timeOnly: '' };
      const day = String(date.getDate()).padStart(2, '0');
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const year = date.getFullYear();
      
      let hours = date.getHours();
      const minutes = String(date.getMinutes()).padStart(2, '0');
      const ampm = hours >= 12 ? 'PM' : 'AM';
      hours = hours % 12 || 12;
      const strHours = String(hours).padStart(2, '0');
      
      return {
        dateOnly: `${day}/${month}/${year}`,
        timeOnly: `${strHours}:${minutes} ${ampm}`
      };
    } catch {
      return { dateOnly: '-', timeOnly: '' };
    }
  };

  return (
    <div className="premium-page-container">
      <div className="premium-page-header">
        <h1 className="page-title">Manage Showroom Category</h1>
        <div style={{ position: 'relative' }}>
          <button 
            className="btn-premium" 
            onClick={() => setShowDropdown(!showDropdown)}
          >
            + Create <IoIosArrowDown />
          </button>
          {showDropdown && (
            <div style={{
              position: 'absolute', top: '100%', right: 0, marginTop: '0.5rem',
              background: '#151515', border: '1px solid rgba(196,168,130,0.3)',
              borderRadius: '8px', zIndex: 10, width: '200px', overflow: 'hidden'
            }}>
              <button 
                type="button"
                style={{ width: '100%', textAlign: 'left', background: 'none', border: 'none', padding: '0.75rem 1rem', cursor: 'pointer', color: '#e5e5e5', borderBottom: '1px solid rgba(196,168,130,0.1)' }}
                onClick={() => { setShowMainModal(true); setShowDropdown(false); setFormData({...formData, category_type: 0}) }}
              >
                Create Category
              </button>
              <button 
                type="button"
                style={{ width: '100%', textAlign: 'left', background: 'none', border: 'none', padding: '0.75rem 1rem', cursor: 'pointer', color: '#e5e5e5' }}
                onClick={() => { setShowSubModal(true); setShowDropdown(false); }}
              >
                Create Sub Category
              </button>
            </div>
          )}
        </div>
      </div>

      <div className="premium-controls-bar">
        <div className="showroom-tabs" style={{ display: 'flex', gap: '0.75rem' }}>
          <button className={`tab-pill ${activeTab === 'All' ? 'active' : ''}`} onClick={() => setActiveTab('All')}>All Category</button>
          <button className={`tab-pill ${activeTab === 'Main Category' ? 'active' : ''}`} onClick={() => setActiveTab('Main Category')}>Main Category</button>
          <button className={`tab-pill ${activeTab === 'Sub Category' ? 'active' : ''}`} onClick={() => setActiveTab('Sub Category')}>Sub Category</button>
        </div>
        <div className="showroom-search search-box">
          <BiSearch className="search-icon" size={20} />
          <input 
            type="text" 
            className="search-input"
            placeholder="Search here" 
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </div>
      </div>

      <div className="premium-table-wrapper animate-fade-up">
          <table className="premium-table">
            <thead>
              <tr>
                <th style={{ width: '4%', textAlign: 'center' }}>
                  <input type="checkbox" className="premium-checkbox" />
                </th>
                <th style={{ width: '12%' }}>Status</th>
                <th style={{ width: '14%' }}>Type</th>
                <th style={{ width: '20%' }}>Category Title</th>
                <th style={{ width: '24%' }}>Description</th>
                <th style={{ width: '16%' }}>Created On</th>
                <th style={{ width: '10%', textAlign: 'center' }}>Action</th>
              </tr>
            </thead>
            <tbody>
              {currentCategories.length > 0 ? currentCategories.map((cat) => {
                const formattedDate = formatDate(cat.created_on);
                return (
                  <tr key={cat.id}>
                    <td style={{ textAlign: 'center' }}>
                      <input type="checkbox" className="premium-checkbox" />
                    </td>
                    <td>
                      <div className="switch-container">
                        {/* eslint-disable-next-line jsx-a11y/label-has-associated-control */}
                        <label htmlFor={`status-toggle-${cat.id}`} className="switch" style={{ margin: 0 }}>
                          <span style={{ position: 'absolute', width: '1px', height: '1px', padding: 0, margin: '-1px', overflow: 'hidden', clip: 'rect(0, 0, 0, 0)', border: 0 }}>Toggle Status</span>
                          <input 
                            id={`status-toggle-${cat.id}`} 
                            type="checkbox" 
                            checked={cat.status === 1} 
                            onChange={() => handleStatusToggle(cat)} 
                          />
                          <span className="slider"></span>
                        </label>
                        <span className="switch-label">
                          {cat.status === 1 ? 'Active' : 'Inactive'}
                        </span>
                      </div>
                    </td>
                    <td>
                      <span className={`badge-type ${cat.category_type === 0 ? 'main' : 'sub'}`}>
                        {cat.category_type === 0 ? 'Main Category' : 'Sub Category'}
                      </span>
                    </td>
                    <td>
                      {cat.category_type === 0 ? (
                        <div style={{ fontWeight: '600', color: '#f5f0e8', fontSize: '12.5px' }}>{cat.category}</div>
                      ) : (
                        <div className="category-name-cell">
                          <span className="category-parent-text">{cat.parent_category_name}</span>
                          <span className="category-title-text">{cat.category}</span>
                        </div>
                      )}
                    </td>
                    <td>
                      <div 
                        className="text-truncate-cell" 
                        title={cat.description?.replace(/<[^>]+>/g, '')}
                      >
                        {cat.description?.replace(/<[^>]+>/g, '') || '-'}
                      </div>
                    </td>
                    <td className="nowrap-cell">
                      <div style={{ color: '#f5f0e8', fontWeight: 600, fontSize: '12px', lineHeight: '1.3' }}>
                        {formattedDate.dateOnly}
                      </div>
                      {formattedDate.timeOnly && (
                        <div style={{ color: '#888', fontSize: '10.5px', marginTop: '1px' }}>
                          {formattedDate.timeOnly}
                        </div>
                      )}
                    </td>
                    <td style={{ textAlign: 'center' }}>
                      <div className="inline-actions-container" style={{ justifyContent: 'center' }}>
                        <button className="btn-inline-action edit-action" title="Edit" onClick={() => setEditCatTarget(cat)}>
                          <BiEdit size={14} />
                        </button>
                        <button className="btn-inline-action delete-action" title="Delete" onClick={() => handleDelete(cat)}>
                          <BiTrash size={14} />
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              }) : (
                <tr>
                  <td colSpan="7" style={{ textAlign: 'center', padding: '3rem', color: '#888' }}>
                    {loading ? 'Loading...' : 'No categories found'}
                  </td>
                </tr>
              )}
            </tbody>
          </table>
      </div>

      {/* Pagination Controls */}
      {filteredCategories.length > 0 && (
        <div className="premium-pagination-container" style={{ margin: '1.5rem 0' }}>
          <div className="premium-pagination-info">
            Showing <span>{indexOfFirstItem + 1}</span> to <span>{Math.min(indexOfLastItem, filteredCategories.length)}</span> of <span>{filteredCategories.length}</span> entries
          </div>
          <div className="premium-pagination-list">
            <button 
              className="premium-pagination-btn arrow-btn" 
              onClick={() => setCurrentPage(1)} 
              disabled={currentPage === 1}
            >
              &laquo;
            </button>
            <button 
              className="premium-pagination-btn arrow-btn" 
              onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))} 
              disabled={currentPage === 1}
            >
              &lsaquo;
            </button>
            
            {Array.from({ length: Math.ceil(filteredCategories.length / itemsPerPage) }, (_, idx) => idx + 1)
              .filter(page => {
                const totalPages = Math.ceil(filteredCategories.length / itemsPerPage);
                return page === 1 || page === totalPages || Math.abs(page - currentPage) <= 1;
              })
              .map((page, index, array) => {
                return (
                  <React.Fragment key={page}>
                    {index > 0 && array[index - 1] !== page - 1 && (
                      <span style={{ color: 'rgba(196, 168, 130, 0.4)', padding: '0 0.25rem' }}>...</span>
                    )}
                    <button 
                      className={`premium-pagination-btn ${currentPage === page ? 'active' : ''}`}
                      onClick={() => setCurrentPage(page)}
                    >
                      {page}
                    </button>
                  </React.Fragment>
                );
              })}

            <button 
              className="premium-pagination-btn arrow-btn" 
              onClick={() => setCurrentPage(prev => Math.min(prev + 1, Math.ceil(filteredCategories.length / itemsPerPage)))} 
              disabled={currentPage === Math.ceil(filteredCategories.length / itemsPerPage)}
            >
              &rsaquo;
            </button>
            <button 
              className="premium-pagination-btn arrow-btn" 
              onClick={() => setCurrentPage(Math.ceil(filteredCategories.length / itemsPerPage))} 
              disabled={currentPage === Math.ceil(filteredCategories.length / itemsPerPage)}
            >
              &raquo;
            </button>
          </div>
        </div>
      )}

      {/* Main Category Modal */}
      {showMainModal && (
        <div className="premium-modal-overlay">
          <div className="premium-modal animate-fade-up">
            <div className="premium-modal-header">
              <h3>Create Category</h3>
              <button className="premium-modal-close" onClick={() => setShowMainModal(false)}>&times;</button>
            </div>
            <form onSubmit={handleCreateSubmit}>
              <div className="premium-modal-body">
                <div className="premium-form-group">
                  <label htmlFor="main-category-title">Category Title <span>*</span></label>
                  <input id="main-category-title" type="text" name="category" placeholder="Category Title" value={formData.category} onChange={handleInputChange} required />
                </div>
                <div className="premium-form-group">
                  <label htmlFor="main-category-desc">Short Description <span>*</span></label>
                  <textarea id="main-category-desc" name="description" placeholder="Short Description" rows="4" value={formData.description} onChange={handleInputChange} required></textarea>
                </div>
              </div>
              <div className="premium-modal-footer">
                <button type="button" className="btn-premium-outline" onClick={() => setShowMainModal(false)}>Cancel</button>
                <button type="submit" className="btn-premium">Submit</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Sub Category Modal */}
      {showSubModal && (
        <div className="premium-modal-overlay">
          <div className="premium-modal animate-fade-up">
            <div className="premium-modal-header">
              <h3>Create Sub Category</h3>
              <button className="premium-modal-close" onClick={() => setShowSubModal(false)}>&times;</button>
            </div>
            <form onSubmit={handleCreateSubmit}>
              <div className="premium-modal-body">
                <div className="premium-form-group">
                  <label htmlFor="sub-category-parent">Category <span>*</span></label>
                  <select id="sub-category-parent" name="category_type" value={formData.category_type} onChange={handleInputChange} required>
                    <option value="">Select Category</option>
                    {mainCategories.map(c => (
                      <option key={c.id} value={c.id}>{c.category}</option>
                    ))}
                  </select>
                </div>
                <div className="premium-form-group">
                  <label htmlFor="sub-category-title">Category Title <span>*</span></label>
                  <input id="sub-category-title" type="text" name="category" placeholder="Category Title" value={formData.category} onChange={handleInputChange} required />
                </div>
                <div className="premium-form-group">
                  <label htmlFor="sub-category-desc">Short Description <span>*</span></label>
                  <textarea id="sub-category-desc" name="description" placeholder="Short Description" rows="4" value={formData.description} onChange={handleInputChange} required></textarea>
                </div>
              </div>
              <div className="premium-modal-footer">
                <button type="button" className="btn-premium-outline" onClick={() => setShowSubModal(false)}>Cancel</button>
                <button type="submit" className="btn-premium">Submit</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Edit Confirmation Modal */}
      <ConfirmModal
        isOpen={!!editCatTarget}
        type="edit"
        title="Modify Category"
        itemName={editCatTarget?.category}
        itemType="category"
        onConfirm={handleConfirmEdit}
        onCancel={() => setEditCatTarget(null)}
      />

      {/* Delete Confirmation Modal */}
      <ConfirmModal
        isOpen={!!deleteCatTarget}
        type="delete"
        title="Delete Category"
        itemName={deleteCatTarget?.category}
        itemType="category"
        onConfirm={handleConfirmDelete}
        onCancel={() => setDeleteCatTarget(null)}
      />

    </div>
  );
};

export default ShowroomCategories;
